<template>
  <v-dialog :value="value" persistent max-width="360" @input="emit('input', $event)">
    <v-card class="pa-4">
      <v-card-title class="text-h2">
        {{ title }}
      </v-card-title>
      <v-card-subtitle class="text-body-small mt-2">
        {{ subtitle }}
      </v-card-subtitle>

      <v-card-text class="mt-4">
        <PinPad ref="pinPad" :error="pinError" :disabled="busy" @complete="handlePinComplete" />
      </v-card-text>

      <v-card-actions class="pb-4 px-4">
        <v-btn color="tetrary" :disabled="busy" @click="cancel">
          {{ $t('profile.cAppSecurity.pin.setup.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import PinPad from './PinPad.vue';

const STEP_ENTER = 'enter';
const STEP_CONFIRM = 'confirm';

const props = defineProps<{
  value: boolean;
}>();

const emit = defineEmits<{
  (e: 'input', value: boolean): void;
  (e: 'confirm', pin: string): void;
}>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const vm = getCurrentInstance()!.proxy as any;

const step = ref(STEP_ENTER);
const firstPin = ref<string | null>(null);
const pinError = ref<string | null>(null);
const busy = ref(false);
const pinPad = ref<InstanceType<typeof PinPad> | null>(null);

const title = computed(() =>
  step.value === STEP_ENTER
    ? vm.$t('profile.cAppSecurity.pin.setup.enterTitle')
    : vm.$t('profile.cAppSecurity.pin.setup.confirmTitle')
);

const subtitle = computed(() =>
  step.value === STEP_ENTER
    ? vm.$t('profile.cAppSecurity.pin.setup.enterSubtitle')
    : vm.$t('profile.cAppSecurity.pin.setup.confirmSubtitle')
);

function reset() {
  step.value = STEP_ENTER;
  firstPin.value = null;
  pinError.value = null;
  busy.value = false;
  setTimeout(() => pinPad.value?.reset(), 0);
}

function handlePinComplete(pin: string) {
  if (step.value === STEP_ENTER) {
    firstPin.value = pin;
    step.value = STEP_CONFIRM;
    pinPad.value?.reset();
    return;
  }

  if (pin !== firstPin.value) {
    pinError.value = vm.$t('profile.cAppSecurity.pin.setup.mismatch');
    pinPad.value?.reset();
    return;
  }

  emit('confirm', pin);
}

function cancel() {
  emit('input', false);
}

watch(
  () => props.value,
  open => {
    if (open) {
      reset();
    }
  }
);
</script>
